import { useMemo } from "react"
import HomeLayout from "modules/home-layout"
import useContextDashboard from "./context/dashboard"
import PreferenceContext from "hooks/preferences"
import { useAccountId } from "resources/account"
import Loading from "components/loading"
import { AiContext } from "connected-components/ai-teammate/context/ai-context"
import { useClearDashboardPreferences } from "./hooks/use-clear-dashboard-preferences"
import { DashboardIdProvider } from "./context/dashboard-id"
import { useDashboardPreferencesSync } from "./hooks/use-dashboard-preferences-sync"
import { useModelId } from "./hooks/use-model-id"

function DashboardWithContext({ children }) {
  // This needs to be done here — it must be a child of PreferenceContext.Provider for usePreferences to work.
  const [DashboardContext, dashboardContextValue] = useContextDashboard()
  const isStoreLoaded = useDashboardPreferencesSync()
  const modelId = useModelId()

  const aiContextValue = useMemo(() => ({
    modelId,
    instrument: dashboardContextValue.instrument,
    base: dashboardContextValue.base,
    startDate: dashboardContextValue.startDate,
    endDate: dashboardContextValue.endDate,
    isPreferencesLoaded: dashboardContextValue.isPreferencesLoaded && isStoreLoaded,
    canPinArtifacts: true,
  }), [
    modelId,
    dashboardContextValue.instrument,
    dashboardContextValue.base,
    dashboardContextValue.startDate,
    dashboardContextValue.endDate,
    dashboardContextValue.isPreferencesLoaded,
    isStoreLoaded,
  ])

  if (!isStoreLoaded) {
    return <Loading />
  }

  return (
    <DashboardContext.Provider value={dashboardContextValue}>
      <AiContext.Provider value={aiContextValue}>
        {children}
      </AiContext.Provider>
    </DashboardContext.Provider>
  )
}

export default function DashboardHomeLayout() {
  const accountId = useAccountId()

  // Check for clearPreferences URL param and handle clearing BEFORE any dashboard queries
  // This must be called before the PreferenceContext.Provider to ensure preferences are cleared before they are loaded
  const { isClearing: isPreferencesClearing } = useClearDashboardPreferences()

  const preferencesProps = useMemo(() => ({
    accountId,
    // Since we can only have one dashboard per account, there is no need to have a more complex key.
    // Each context will append its unique identifier to the key.
    // eg. dashboard.asset-view, dashboard.kpi, dashboard.factors-view
    key: "dashboard",
    type: "dashboard",
  }), [accountId])

  if (isPreferencesClearing) {
    return <Loading />
  }

  return (
    <PreferenceContext.Provider value={preferencesProps}>
      <DashboardIdProvider value="default">
        <DashboardWithContext>
          <HomeLayout />
        </DashboardWithContext>
      </DashboardIdProvider>
    </PreferenceContext.Provider>
  )
}
